import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { toast } from './ui/use-toast';
import { Loader2, UserPlus, Search } from 'lucide-react';
import contactService from '../services/contactService';
import { useHubSpot } from '../hooks/useHubSpot';
import { useAuth } from '../contexts/AuthContext';

export default function ContactSelector({ customerId, stopId, onContactsSelected, onClose }) {
  // Handle case where customerId might be an array
  const actualCustomerId = Array.isArray(customerId) ? customerId[0] : customerId;
  
  const [availableContacts, setAvailableContacts] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showNewContactForm, setShowNewContactForm] = useState(false);
  const [newContact, setNewContact] = useState({
    name: '',
    email: '',
    phone: '',
    position: ''
  });
  const [hubspotSearchTerm, setHubspotSearchTerm] = useState('');
  const [hubspotSearchResults, setHubspotSearchResults] = useState([]);
  const [searchingHubspot, setSearchingHubspot] = useState(false);
  const [showHubspotSearch, setShowHubspotSearch] = useState(false);
  const [debounceTimer, setDebounceTimer] = useState(null);
  
  const { searchContacts, hubspotService } = useHubSpot();
  const { user } = useAuth();

  useEffect(() => {
    if (actualCustomerId) {
      fetchCustomerContacts();
    }
  }, [actualCustomerId]);

  const fetchCustomerContacts = async () => {
    setLoading(true);
    try {
      const contacts = await contactService.getCustomerContacts(actualCustomerId);
      // Ensure we always have an array
      const contactsArray = Array.isArray(contacts) ? contacts : [];
      console.log('Fetched customer contacts:', contactsArray);
      setAvailableContacts(contactsArray);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast({
        title: 'Error',
        description: 'Failed to load contacts',
        variant: 'destructive'
      });
      setAvailableContacts([]); // Ensure array on error
    } finally {
      setLoading(false);
    }
  };

  const handleContactToggle = (contact) => {
    const contactId = contact.contact_id || contact.id; // Handle both contact_id and id
    const newSelected = new Map(selectedContacts);
    if (newSelected.has(contactId)) {
      newSelected.delete(contactId);
    } else {
      newSelected.set(contactId, {
        contact,
        engagementType: 'general',
        notes: ''
      });
    }
    setSelectedContacts(newSelected);
  };

  const updateContactData = (contactId, field, value) => {
    const newSelected = new Map(selectedContacts);
    if (newSelected.has(contactId)) {
      const data = newSelected.get(contactId);
      data[field] = value;
      newSelected.set(contactId, data);
      setSelectedContacts(newSelected);
    }
  };

  const handleCreateNewContact = async () => {
    if (!newContact.name || !newContact.email) {
      toast({
        title: 'Error',
        description: 'Name and email are required',
        variant: 'destructive'
      });
      return;
    }

    try {
      const contactData = {
        ...newContact,
        customer_id: actualCustomerId
      };
      const created = await contactService.createContact(contactData);
      console.log('Created contact response:', created);
      setAvailableContacts([...availableContacts, created]);
      setShowNewContactForm(false);
      setNewContact({ name: '', email: '', phone: '', position: '' });
      toast({
        title: 'Success',
        description: 'Contact created successfully'
      });
    } catch (error) {
      console.error('Error creating contact:', error);
      toast({
        title: 'Error',
        description: 'Failed to create contact',
        variant: 'destructive'
      });
    }
  };

  const associateContacts = async () => {
    if (!actualCustomerId && !stopId) {
      toast({
        title: 'Error',
        description: 'Customer ID or Stop ID is required',
        variant: 'destructive'
      });
      console.error('Invalid customerId:', actualCustomerId, 'stopId:', stopId);
      return;
    }

    if (selectedContacts.size === 0) {
      toast({
        title: 'No contacts selected',
        description: 'Please select at least one contact',
        variant: 'destructive'
      });
      return;
    }

    setSubmitting(true);
    let successCount = 0;
    const errors = [];

    try {
      // Prepare data in the format backend expects
      const contactIds = Array.from(selectedContacts.keys()).filter(id => id != null && id !== undefined);
      const notes = Array.from(selectedContacts.values())[0]?.notes || ''; // Use first contact's notes
      const role = user?.role || 'BM';
      
      // Sync engagement to HubSpot (works with or without stopId)
      console.log('🔗 ContactSelector - About to sync engagement with:', {
        stopId,
        contactIds,
        notes,
        actualCustomerId
      });
      
      await contactService.syncEngagementToHubSpot(stopId, contactIds, notes, actualCustomerId);
      successCount = contactIds.length;
      
      if (successCount > 0) {
        console.log('🎉 ContactSelector SUCCESS - Contacts associated for customer:', actualCustomerId);
        
        toast({
          title: 'Success',
          description: `Synced ${successCount} contact(s) engagement`
        });
        if (onContactsSelected) {
          console.log('📡 ContactSelector - Calling onContactsSelected callback');
          onContactsSelected({ success: successCount, errors });
        }
        setSelectedContacts(new Map());
        if (onClose) {
          console.log('📡 ContactSelector - Closing dialog');
          onClose();
        }
      }
      
      if (errors.length > 0) {
        toast({
          title: 'Some contacts failed',
          description: errors.join(', '),
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error associating contacts:', error);
      toast({
        title: 'Error',
        description: 'Failed to associate contacts',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const performHubspotSearch = async (searchTerm) => {
    if (!searchTerm.trim() || !hubspotService) {
      setHubspotSearchResults([]);
      return;
    }

    setSearchingHubspot(true);
    setHubspotSearchResults([]);

    try {
      // Use the new search API
      const response = await searchContacts(searchTerm, {
        limit: 20
      });
      
      if (response.status === 'success' && response.data?.results) {
        const formattedContacts = response.data.results.map(contact => ({
          id: contact.id,
          name: `${contact.properties.firstname || ''} ${contact.properties.lastname || ''}`.trim() || 'Unknown Contact',
          email: contact.properties.email,
          phone: contact.properties.phone || '',
          position: contact.properties.jobtitle || '',
          company: contact.properties.company || '',
          source: 'hubspot'
        }));
        
        console.log('Formatted HubSpot contacts:', formattedContacts);
        
        setHubspotSearchResults(formattedContacts);
        
        if (formattedContacts.length === 0) {
          toast({
            title: 'No results',
            description: 'No contacts found matching your search',
          });
        }
      } else {
        setHubspotSearchResults([]);
        toast({
          title: 'No results',
          description: 'No contacts found',
        });
      }
    } catch (error) {
      console.error('Error searching HubSpot:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to search contacts',
        variant: 'destructive'
      });
    } finally {
      setSearchingHubspot(false);
    }
  };

  const handleHubspotSearch = useCallback((value) => {
    setHubspotSearchTerm(value);
    
    // Clear previous timer
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
    
    // Set new timer for debounced search
    const newTimer = setTimeout(() => {
      performHubspotSearch(value);
    }, 300); // 300ms debounce
    
    setDebounceTimer(newTimer);
  }, [debounceTimer]);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [debounceTimer]);

  const addHubspotContactToAvailable = (hubspotContact) => {
    console.log('Adding HubSpot contact to available:', hubspotContact);
    
    // Check if contact already exists in available contacts
    const exists = availableContacts.some(c => 
      c.email.toLowerCase() === hubspotContact.email.toLowerCase()
    );
    
    if (!exists) {
      setAvailableContacts([...availableContacts, hubspotContact]);
    }
    
    // Clear HubSpot search
    setHubspotSearchResults([]);
    setHubspotSearchTerm('');
    setShowHubspotSearch(false);
    
    // Auto-select the newly added contact
    handleContactToggle(hubspotContact);
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <Card className="w-full max-w-3xl">
      <CardHeader>
        <CardTitle>Select Contacts for This Stop</CardTitle>
        <CardDescription>
          Choose contacts to associate with this stop and specify engagement details
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-end space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowHubspotSearch(!showHubspotSearch)}
          >
            <Search className="h-4 w-4 mr-1" />
            Search Contacts
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowNewContactForm(!showNewContactForm)}
          >
            <UserPlus className="h-4 w-4 mr-1" />
            New Contact
          </Button>
        </div>

        {showHubspotSearch && (
          <Card className="p-4 bg-muted/50">
            <h4 className="font-semibold mb-3">Search Contacts</h4>
            <div className="flex space-x-2">
              <Input
                placeholder="Enter name, email, or company..."
                value={hubspotSearchTerm}
                onChange={(e) => handleHubspotSearch(e.target.value)}
                className="flex-1"
              />
              {searchingHubspot && (
                <div className="flex items-center text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Searching...
                </div>
              )}
            </div>
            
            {hubspotSearchTerm && !searchingHubspot && hubspotSearchResults.length === 0 && (
              <p className="text-sm text-muted-foreground mt-4">
                No contacts found. Try a different search term.
              </p>
            )}
            
            {hubspotSearchResults.length > 0 && (
              <div className="mt-4 space-y-2 max-h-60 overflow-y-auto">
                <p className="text-sm text-muted-foreground mb-2">
                  Found {hubspotSearchResults.length} contact(s):
                </p>
                {hubspotSearchResults.map((contact) => (
                  <Card key={contact.id} className="p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="font-medium">{contact.name}</p>
                        <p className="text-sm text-muted-foreground">{contact.email}</p>
                        {contact.position && (
                          <Badge variant="secondary" className="mt-1 mr-2">
                            {contact.position}
                          </Badge>
                        )}
                        {contact.company && (
                          <Badge variant="outline" className="mt-1">
                            {contact.company}
                          </Badge>
                        )}
                      </div>
                      <Button
                        size="sm"
                        onClick={() => addHubspotContactToAvailable(contact)}
                      >
                        Add to List
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </Card>
        )}

        {showNewContactForm && (
          <Card className="p-4 bg-muted/50">
            <h4 className="font-semibold mb-3">Create New Contact</h4>
            <div className="grid grid-cols-2 gap-3">
              <Input
                placeholder="Name *"
                value={newContact.name}
                onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
              />
              <Input
                placeholder="Email *"
                type="email"
                value={newContact.email}
                onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
              />
              <Input
                placeholder="Phone"
                value={newContact.phone}
                onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
              />
              <Input
                placeholder="Position"
                value={newContact.position}
                onChange={(e) => setNewContact({ ...newContact, position: e.target.value })}
              />
            </div>
            <div className="flex justify-end space-x-2 mt-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowNewContactForm(false);
                  setNewContact({ name: '', email: '', phone: '', position: '' });
                }}
              >
                Cancel
              </Button>
              <Button size="sm" onClick={handleCreateNewContact}>
                Create Contact
              </Button>
            </div>
          </Card>
        )}

        <div className="space-y-3 max-h-96 overflow-y-auto">
          {availableContacts.length === 0 ? (
            <p className="text-center text-muted-foreground py-4">
              No contacts added yet. Use 'Search Contacts' to find contacts.
            </p>
          ) : (
            availableContacts.map(contact => {
              const contactId = contact.contact_id || contact.id; // Handle both contact_id and id
              return (
              <Card
                key={contactId}
                className={`p-4 transition-colors ${
                  selectedContacts.has(contactId) ? 'bg-primary/5 border-primary' : ''
                }`}
              >
                <div className="space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3 flex-1">
                      <Checkbox
                        checked={selectedContacts.has(contactId)}
                        onCheckedChange={() => handleContactToggle(contact)}
                      />
                      <div className="flex-1">
                        <p className="font-medium">{contact.name}</p>
                        <p className="text-sm text-muted-foreground">{contact.email}</p>
                        {contact.position && (
                          <Badge variant="secondary" className="mt-1">
                            {contact.position}
                          </Badge>
                        )}
                        {contact.phone && (
                          <p className="text-sm text-muted-foreground mt-1">{contact.phone}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {selectedContacts.has(contactId) && (
                    <div className="ml-6 space-y-2 pt-2 border-t">
                      <div className="flex items-center space-x-2">
                        <label className="text-sm font-medium">Engagement Type:</label>
                        <Select
                          value={selectedContacts.get(contactId).engagementType}
                          onValueChange={(value) => updateContactData(contactId, 'engagementType', value)}
                        >
                          <SelectTrigger className="w-40">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="general">General</SelectItem>
                            <SelectItem value="meeting">Meeting</SelectItem>
                            <SelectItem value="call">Call</SelectItem>
                            <SelectItem value="email">Email</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-sm font-medium">
                          Notes: 
                          <span className="text-xs text-muted-foreground ml-1">
                            ({user?.role === 'BM' ? 'BM' : 'RSM'} notes)
                          </span>
                        </label>
                        <Textarea
                          placeholder={`Add ${user?.role === 'BM' ? 'BM' : 'RSM'} notes about this engagement...`}
                          value={selectedContacts.get(contactId).notes}
                          onChange={(e) => updateContactData(contactId, 'notes', e.target.value)}
                          className="mt-1"
                          rows={2}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </Card>
              );
            })
          )}
        </div>

        <div className="flex justify-between items-center pt-4 border-t">
          <p className="text-sm text-muted-foreground">
            {selectedContacts.size} contact(s) selected
          </p>
          <div className="space-x-2">
            {onClose && (
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
            )}
            <Button
              onClick={associateContacts}
              disabled={submitting || selectedContacts.size === 0}
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Associating...
                </>
              ) : (
                `Add ${selectedContacts.size} Contact(s)`
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}